import type { ComponentType } from 'react'
import type { RouteSeo } from './lib/seo'
import { siteJsonLd } from './lib/seo'
import { resolveSeoTarget, type SeoTarget } from './lib/seo-profiles'
import { contentRoutes } from './content-routes'
import { allContent, contentPath, researchPapers } from './lib/content'
import { SITE_URL, SITE_NAME } from './lib/site'
import { langfuseAlternativeFaq } from './content/langfuse-alternative-faq'
import { langyFaq } from './content/langy-faq'
import {
  claudeCodeUsageFaq,
  codexUsageFaq,
  openCodeUsageFaq,
} from './content/claude-code-usage-faq'
import { jevBenchmarkFaq } from './content/jev-benchmark-faq'
import { jevVsAllFaq } from './content/jev-vs-all-faq'
import { jevVsLlmFaq } from './content/jev-vs-llm-faq'
import { PAGE_MODELS, modelHref, slugOf } from './components/bench/registry'

function faqJsonLd(faq: { q: string; a: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faq.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  }
}

export type RouteDef = {
  /** canonical path */
  path: string
  /** extra paths that render the same page (canonical still points to `path`) */
  aliases?: string[]
  /**
   * Per-route component, either eager (content routes wrap their MDX body)
   * or a dynamic-import factory that Vite splits into its own chunk. Use
   * `loadRouteComponent(route)` to resolve either form uniformly.
   */
  Component?: ComponentType
  load?: () => Promise<ComponentType>
  /**
   * The source file backing `load`, used by the prerender step to look up
   * the route's emitted chunk in Vite's manifest and inject a per-route
   * `<link rel="modulepreload">` so the entry-to-page-chunk waterfall
   * disappears. Omit for routes that ship eagerly via `Component`.
   */
  chunkSrc?: string
  seo: RouteSeo
  /** include in sitemap.xml (default true) */
  sitemap?: boolean
  /** resolve any code-split content this route needs before render/hydrate */
  preload?: () => Promise<void>
}

/**
 * Resolve a route's React component, whether it ships eagerly (content pages)
 * or as a dynamic-import chunk (static pages). Both entry-server and
 * entry-client await this before render/hydrate, so React never observes an
 * unresolved Suspense boundary and the prerender stays synchronous-at-render.
 */
export async function loadRouteComponent(route: RouteDef): Promise<ComponentType> {
  if (route.Component) return route.Component
  if (route.load) return route.load()
  throw new Error(`route ${route.path} has neither Component nor load`)
}

const staticRoutes: RouteDef[] = [
  {
    path: '/',
    load: () => import('./pages/HomePage').then((m) => m.HomePage),
    chunkSrc: 'src/pages/HomePage.tsx',
    seo: {
      keyphrase: 'agent observability',
      title: 'Agent Observability & Evals for AI Platform Teams',
      description:
        'Trace, test, route and govern every LLM call in your company, from your own agents to the coding assistants your engineers use. Built for AI platform teams.',
      jsonLd: siteJsonLd(),
    },
  },
  {
    // Prerenders to dist/404.html, which Cloudflare Pages serves with a real
    // 404 status for any unmatched path. Without it the host assumes a SPA and
    // answers every missing page with index.html and a 200 (a soft 404 that
    // crawlers index). matchRoute also falls back to this route on the client.
    path: '/404',
    load: () => import('./pages/NotFoundPage').then((m) => m.NotFoundPage),
    chunkSrc: 'src/pages/NotFoundPage.tsx',
    sitemap: false,
    seo: {
      title: 'Page not found | LangWatch',
      description:
        'This page does not exist on langwatch.ai. Head back to the homepage, the blog, the integrations overview, or pricing to find what you were looking for.',
      noindex: true,
    },
  },
  {
    path: '/customers',
    load: () => import('./pages/CustomersPage').then((m) => m.CustomersPage),
    chunkSrc: 'src/pages/CustomersPage.tsx',
    seo: {
      keyphrase: 'customer stories',
      title: 'Customer stories | LangWatch',
      description:
        'How AI teams use LangWatch to evaluate, monitor, and ship reliable agents in production. Read the customer stories and the results they reached.',
    },
  },
  {
    // /agentic-ai-testing is the live, indexed URL; /scenario is the nicer alias.
    path: '/agentic-ai-testing',
    aliases: ['/scenario'],
    load: () => import('./pages/ScenarioPage').then((m) => m.ScenarioPage),
    chunkSrc: 'src/pages/ScenarioPage.tsx',
    seo: {
      keyphrase: 'agent simulation testing',
      title: 'Agent simulation testing | LangWatch',
      description:
        'Test AI agents with scenario simulations before they reach production. Simulate real conversations, catch regressions, and prove agent quality every release.',
    },
  },
  {
    path: '/gateway',
    aliases: ['/ai-gateway', '/ai-governance'],
    load: () => import('./pages/AiGatewayPage').then((m) => m.AiGatewayPage),
    chunkSrc: 'src/pages/AiGatewayPage.tsx',
    seo: {
      keyphrase: 'ai gateway',
      title: 'AI Gateway | LangWatch',
      description:
        'An AI gateway that puts every AI call on a budget: limits per person, team and customer, your own provider keys, 11 microseconds of overhead, zero markup.',
    },
  },
  {
    path: '/agent-governance-playbook',
    load: () => import('./pages/GovernancePlaybookPage').then((m) => m.GovernancePlaybookPage),
    chunkSrc: 'src/pages/GovernancePlaybookPage.tsx',
    seo: {
      keyphrase: 'ai agent governance',
      title: 'The Agent Governance Playbook | LangWatch',
      description:
        'Free enterprise whitepaper: six requirements, an alert-to-enforcement maturity ladder, and a 12-question self-assessment for governing every AI agent you run.',
    },
  },
  {
    path: '/llm-evaluation',
    load: () => import('./pages/EvaluationPage').then((m) => m.EvaluationPage),
    chunkSrc: 'src/pages/EvaluationPage.tsx',
    seo: {
      keyphrase: 'llm evaluation',
      title: 'LLM evaluation | LangWatch',
      description:
        'Evaluate LLM and agent quality with offline and online evals, custom evaluators, and datasets. Catch quality regressions before your users ever do.',
    },
  },
  {
    path: '/llm-observability',
    load: () => import('./pages/ObservabilityPage').then((m) => m.ObservabilityPage),
    chunkSrc: 'src/pages/ObservabilityPage.tsx',
    seo: {
      keyphrase: 'llm observability',
      title: 'LLM observability | LangWatch',
      description:
        'OpenTelemetry-native LLM observability. Trace every agent step, monitor cost and latency, and debug AI in production with full end-to-end visibility.',
    },
  },
  {
    // /langy is the name people search and link; the keyword URL is the alias.
    path: '/langy',
    aliases: ['/automated-ai-engineer'],
    load: () => import('./pages/LangyPage').then((m) => m.LangyPage),
    chunkSrc: 'src/pages/LangyPage.tsx',
    seo: {
      keyphrase: 'automated ai engineer',
      title: 'Automated AI engineer: meet Langy | LangWatch',
      description:
        'Langy is an automated AI engineer inside LangWatch. It reads your traces, writes the tests, opens the pull request, and proves the fix in your CI.',
      jsonLd: [faqJsonLd(langyFaq)],
    },
  },
  {
    path: '/instant-evals',
    load: () => import('./pages/InstantEvalsPage').then((m) => m.InstantEvalsPage),
    chunkSrc: 'src/pages/InstantEvalsPage.tsx',
    seo: {
      keyphrase: 'instant evals',
      title: 'Instant Evals | LangWatch',
      description:
        'Instant Evals: search your entire production history with on-demand evals. Ask one question of every conversation, thread or LLM call, get matches in minutes.',
    },
  },
  {
    path: '/brand',
    aliases: ['/press', '/press-kit'],
    load: () => import('./pages/BrandPage').then((m) => m.BrandPage),
    chunkSrc: 'src/pages/BrandPage.tsx',
    seo: {
      keyphrase: 'langwatch brand assets',
      title: 'Brand assets and press kit | LangWatch',
      description:
        'LangWatch brand assets and press kit: download the logo and watchtower mark in SVG and PNG, copy our colours and company description, and reach the press team.',
    },
  },
  {
    path: '/design-system',
    load: () => import('./pages/DesignSystemPage').then((m) => m.DesignSystemPage),
    chunkSrc: 'src/pages/DesignSystemPage.tsx',
    sitemap: false,
    seo: {
      title: 'Design system | LangWatch',
      description:
        'The LangWatch marketing site design system: typography, color, components, motion, and the usage guidelines behind the brand.',
    },
  },
  {
    // Internal fixture for the build-time mermaid pipeline (no published post
    // uses a mermaid fence yet). See specs/mermaid-diagrams.feature.
    path: '/dev/mermaid-demo',
    load: () => import('./pages/MermaidDemoPage').then((m) => m.MermaidDemoPage),
    chunkSrc: 'src/pages/MermaidDemoPage.tsx',
    sitemap: false,
    seo: {
      title: 'Mermaid diagram rendering demo | LangWatch',
      description:
        'Internal demo page proving fenced mermaid blocks in MDX render to static inline SVG diagrams at build time, with no client-side mermaid bundle.',
    },
  },
  {
    // Internal fixture for the build-time syntax highlighting pipeline (no
    // published post has code blocks yet). See specs/code-highlighting.feature.
    path: '/dev/code-highlight-demo',
    load: () => import('./pages/CodeHighlightDemoPage').then((m) => m.CodeHighlightDemoPage),
    chunkSrc: 'src/pages/CodeHighlightDemoPage.tsx',
    sitemap: false,
    seo: {
      title: 'Code syntax highlighting demo | LangWatch',
      description:
        'Internal demo page proving fenced code blocks in MDX render to highlighted static HTML at build time, with no client-side syntax highlighter bundle.',
    },
  },
  {
    path: '/langfuse-alternative',
    load: () => import('./pages/LangfuseAlternativePage').then((m) => m.LangfuseAlternativePage),
    chunkSrc: 'src/pages/LangfuseAlternativePage.tsx',
    seo: {
      keyphrase: 'langfuse alternative',
      title: 'Langfuse alternative (2026 comparison) | LangWatch',
      description:
        'The Langfuse alternative compared for 2026: agent simulations, voice testing, evals product experts run themselves, CI gates, licensing, and EU hosting.',
      jsonLd: [
        {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: langfuseAlternativeFaq.map((f) => ({
            '@type': 'Question',
            name: f.q,
            acceptedAnswer: { '@type': 'Answer', text: f.a },
          })),
        },
      ],
    },
  },
  {
    path: '/braintrust-alternative',
    load: () => import('./pages/BraintrustAlternativePage').then((m) => m.BraintrustAlternativePage),
    chunkSrc: 'src/pages/BraintrustAlternativePage.tsx',
    seo: {
      keyphrase: 'braintrust alternative',
      title: 'Braintrust alternative | LangWatch',
      description:
        'Stop scoring failures. Start preventing them. LangWatch is the Braintrust alternative with agent simulation, open source, and a workflow for the whole team.',
    },
  },
  {
    path: '/langsmith-alternative',
    load: () => import('./pages/LangsmithAlternativePage').then((m) => m.LangsmithAlternativePage),
    chunkSrc: 'src/pages/LangsmithAlternativePage.tsx',
    seo: {
      keyphrase: 'langsmith alternative',
      title: 'LangSmith alternative | LangWatch',
      description:
        'Framework-agnostic, OpenTelemetry-native, open source. The LangSmith alternative built for agent simulation testing without LangChain ecosystem lock-in.',
    },
  },
  {
    path: '/arize-alternative',
    load: () => import('./pages/ArizeAlternativePage').then((m) => m.ArizeAlternativePage),
    chunkSrc: 'src/pages/ArizeAlternativePage.tsx',
    seo: {
      keyphrase: 'arize alternative',
      title: 'Arize alternative | LangWatch',
      description:
        'Built for LLMs, not retrofitted from classic ML monitoring. The Arize alternative with agent simulation testing, prompt optimization, and a hybrid workflow.',
    },
  },
  {
    path: '/humanloop-alternative',
    load: () => import('./pages/HumanloopAlternativePage').then((m) => m.HumanloopAlternativePage),
    chunkSrc: 'src/pages/HumanloopAlternativePage.tsx',
    seo: {
      keyphrase: 'humanloop alternative',
      title: 'Humanloop alternative | LangWatch',
      description:
        'Multi-turn, multi-tool, open source. The Humanloop alternative with full agent simulation, OpenTelemetry-native tracing, and built-in red teaming.',
    },
  },
  {
    path: '/better-agents-manifesto',
    load: () => import('./pages/BetterAgentsManifestoPage').then((m) => m.BetterAgentsManifestoPage),
    chunkSrc: 'src/pages/BetterAgentsManifestoPage.tsx',
    seo: {
      keyphrase: 'better agents manifesto',
      title: 'The Better Agents Manifesto | LangWatch',
      description:
        'Four values for teams building agents that ship. Systematic quality, real scenarios, business metrics, incremental over premature AGI.',
    },
  },
  {
    path: '/sdks',
    load: () => import('./pages/SdksPage').then((m) => m.SdksPage),
    chunkSrc: 'src/pages/SdksPage.tsx',
    seo: {
      keyphrase: 'llm sdks',
      title: 'SDKs | LangWatch',
      description:
        'LangWatch Python and TypeScript SDKs for tracing, evaluating, and optimizing agents. OpenTelemetry-native and framework-agnostic. Quickstart in five minutes.',
    },
  },
  {
    path: '/integrations',
    load: () => import('./pages/IntegrationsPage').then((m) => m.IntegrationsPage),
    chunkSrc: 'src/pages/IntegrationsPage.tsx',
    seo: {
      keyphrase: 'llm integrations',
      title: 'Integrations | LangWatch',
      description:
        'Every integration LangWatch ships: Python and TypeScript SDKs, OpenTelemetry, OpenAI, Anthropic, AWS Bedrock, Azure, Vertex, LangGraph, CrewAI, DSPy, and more.',
    },
  },
  {
    // Live, indexed URL is /ai-agent-testing-training; /evals-training is the
    // net-new alias (canonical stays the live one to preserve ranking).
    path: '/ai-agent-testing-training',
    aliases: ['/evals-training'],
    load: () => import('./pages/EvalsTrainingPage').then((m) => m.EvalsTrainingPage),
    chunkSrc: 'src/pages/EvalsTrainingPage.tsx',
    seo: {
      keyphrase: 'ai agent testing training',
      title: 'AI agent testing training for your team | LangWatch',
      description:
        'A guided curriculum for teams evaluating LLM agents. Six modules from scoring to scenario design and CI gates. Workshop or self-paced.',
    },
  },
  {
    path: '/about-us',
    load: () => import('./pages/AboutUsPage').then((m) => m.AboutUsPage),
    chunkSrc: 'src/pages/AboutUsPage.tsx',
    seo: {
      title: 'About LangWatch | The team building reliable AI',
      description:
        'Meet the team behind LangWatch, the AI agent engineering platform. Our mission: make AI reliable enough teams can focus on strategy, not firefighting.',
    },
  },
  {
    path: '/pricing',
    load: () => import('./pages/PricingPage').then((m) => m.PricingPage),
    chunkSrc: 'src/pages/PricingPage.tsx',
    seo: {
      keyphrase: 'langwatch pricing',
      title: 'LangWatch pricing: free to start, scale by usage',
      description:
        'Start free with the LangWatch Developer plan. Paid plans from €29/month add unlimited evaluations and simulations, with enterprise security and self-hosting.',
    },
  },
  {
    path: '/get-a-demo',
    load: () => import('./pages/GetADemoPage').then((m) => m.GetADemoPage),
    chunkSrc: 'src/pages/GetADemoPage.tsx',
    seo: {
      keyphrase: 'langwatch demo',
      title: 'Book a LangWatch demo | Talk to the team',
      description:
        'See LangWatch on your stack: real evaluations and agent simulations, pricing, security, and procurement. Book a 30-minute session with our team.',
    },
  },
  {
    path: '/partners',
    load: () => import('./pages/PartnersPage').then((m) => m.PartnersPage),
    chunkSrc: 'src/pages/PartnersPage.tsx',
    seo: {
      keyphrase: 'partner program',
      title: 'Partner program | LangWatch',
      description:
        'Join the LangWatch partner program. Refer, resell, or integrate, and bring reliable AI to your customers with revenue share up to 45% and sales support.',
    },
  },
  {
    path: '/trust-center',
    load: () => import('./pages/TrustCenterPage').then((m) => m.TrustCenterPage),
    chunkSrc: 'src/pages/TrustCenterPage.tsx',
    seo: {
      keyphrase: 'trust center',
      title: 'Trust center | LangWatch security & compliance',
      description:
        'LangWatch security and compliance: ISO 27001, GDPR, SOC 2, encryption, RBAC, SSO, and self-hosting. Read our trust report and deployment options.',
    },
  },
  {
    path: '/enterprise',
    load: () => import('./pages/EnterprisePage').then((m) => m.EnterprisePage),
    chunkSrc: 'src/pages/EnterprisePage.tsx',
    seo: {
      keyphrase: 'enterprise ai platform',
      title: 'Enterprise AI platform: govern agents at scale | LangWatch',
      description:
        'Ship agentic AI with enterprise security and governance: SSO, SCIM, custom RBAC, audit logs, self-hosting, and EU data residency across the AI lifecycle.',
    },
  },
  {
    path: '/comparison',
    load: () => import('./pages/ComparisonPage').then((m) => m.ComparisonPage),
    chunkSrc: 'src/pages/ComparisonPage.tsx',
    seo: {
      keyphrase: 'langwatch vs langsmith',
      title: 'LangWatch vs LangSmith vs LangFuse | Comparison',
      description:
        'Compare LangWatch, LangSmith, and LangFuse across LLM observability, evaluations, guardrails, and production readiness, feature by feature.',
    },
  },
  {
    path: '/benchmarks',
    load: () => import('./pages/BenchmarksPage').then((m) => m.BenchmarksPage),
    chunkSrc: 'src/pages/BenchmarksPage.tsx',
    seo: {
      // a phrase the page body says (the lede), so the content check can find it
      keyphrase: 'ai model benchmark',
      title: 'AI model benchmarks on real tasks | LangWatch',
      description:
        'Every AI model benchmark LangWatch has run: which models we compared, on how many tasks, and what we found, with the method and caveats on each page.',
    },
  },
  {
    // PREVIEW (component-library prototype): the jev-vs-llm benchmark rebuilt from src/components/bench/ and one
    // config file. Out of sitemap.xml and llms.txt, and noindexed in public/_headers.
    path: '/benchmarks/jev-vs-llm-v2',
    load: () => import('./pages/benchmarkTemplateRoute').then((m) => m.templatePage(() => import('./components/bench/configs/jev-vs-llm'))()),
    chunkSrc: 'src/pages/benchmarkTemplateRoute.tsx',
    sitemap: false,
    seo: {
      title: 'Jev vs frontier LLMs, new layout preview | LangWatch',
      description:
        'Preview of the benchmark page built from shared components: Jev against frontier LLMs per task, with intervals, tie tiers, cost and latency trade-offs.',
    },
  },
  {
    // PREVIEW (component-library prototype): the jev-vs-all benchmark rebuilt from src/components/bench/ and one
    // config file. Out of sitemap.xml and llms.txt, and noindexed in public/_headers.
    path: '/benchmarks/jev-vs-all-v2',
    load: () => import('./pages/benchmarkTemplateRoute').then((m) => m.templatePage(() => import('./components/bench/configs/jev-vs-all'))()),
    chunkSrc: 'src/pages/benchmarkTemplateRoute.tsx',
    sitemap: false,
    seo: {
      title: 'Jev vs open models up to 28B, new layout preview | LangWatch',
      description:
        'Preview of the benchmark page built from shared components: Jev against open models up to 28B per task, with intervals, tie tiers and latency per hardware tier.',
    },
  },
  {
    // PREVIEW (component-library prototype): the jev-benchmark rebuilt from src/components/bench/ and one config
    // file. Out of sitemap.xml and llms.txt, and noindexed in public/_headers.
    path: '/benchmarks/jev-benchmark-v2',
    load: () => import('./pages/benchmarkTemplateRoute').then((m) => m.templatePage(() => import('./components/bench/configs/jev-benchmark'))()),
    chunkSrc: 'src/pages/benchmarkTemplateRoute.tsx',
    sitemap: false,
    seo: {
      title: 'Jev vs models that run on your laptop, preview | LangWatch',
      description:
        'Preview of the benchmark page built from shared components: Jev against open models under 1B parameters per task, with intervals, tie tiers and latency.',
    },
  },
  {
    // PREVIEW (component-library prototype): which model should judge LangWatch's three judge jobs, as one config
    // file plus the judge benchmark's export (aggregates only). Out of sitemap.xml and llms.txt, and noindexed in
    // public/_headers until the page is signed off.
    path: '/benchmarks/llm-as-a-judge',
    // the first URL, 301ed here by public/_redirects
    aliases: ['/benchmarks/judge-benchmark'],
    load: () => import('./pages/benchmarkTemplateRoute').then((m) => m.templatePage(() => import('./components/bench/configs/judge'))()),
    chunkSrc: 'src/pages/benchmarkTemplateRoute.tsx',
    sitemap: false,
    seo: {
      title: 'LLM-as-a-Judge Pareto Frontier benchmark | LangWatch',
      description:
        'Which model should judge LLM-as-a-judge evals, Scenario simulations and Instant Evals search: nine models on three sets, with intervals, tie tests and cost.',
    },
  },
  {
    // Jev against the decision models built like it (no LLMs) on judge-lab's three sets of LLM-as-a-judge checks, as
    // one config file plus its export (aggregates only). Indexed under the name people search for; the first URL
    // stays an alias and public/_redirects 301s it here.
    path: '/benchmarks/jev-alternatives',
    aliases: ['/benchmarks/jev-class-hard'],
    load: () => import('./pages/benchmarkTemplateRoute').then((m) => m.templatePage(() => import('./components/bench/configs/jev-class-hard'))()),
    chunkSrc: 'src/pages/benchmarkTemplateRoute.tsx',
    seo: {
      keyphrase: 'jev alternatives',
      title: 'Jev alternatives benchmarked on LLM-as-a-judge | LangWatch',
      description:
        'Jev against the OpenAI Decisions API, Perplexity pplx-decider and 20 other Jev alternatives on three sets of LLM-as-a-judge checks, with cost and latency.',
    },
  },
  {
    // PREVIEW (component-library prototype): the PII benchmark as one config file plus its results
    // file. Cleared for publication, but still a preview route: out of sitemap.xml and llms.txt, and noindexed in public/_headers.
    path: '/benchmarks/pii-detection',
    // the first URL, 301ed here by public/_redirects
    aliases: ['/benchmarks/pii-v2'],
    load: () => import('./pages/benchmarkTemplateRoute').then((m) => m.templatePage(() => import('./components/bench/configs/pii'))()),
    chunkSrc: 'src/pages/benchmarkTemplateRoute.tsx',
    sitemap: false,
    seo: {
      title: 'PII detection benchmark: open and hosted tools | LangWatch',
      description:
        'Open and hosted PII detectors on six tests, scored by how much PII each one leaves in, with intervals, tie tiers and false positives.',
    },
  },
  {
    // PREVIEW (component-library prototype): the curated models first (src/components/bench/curated.ts), then every other model. Shareable, but noindexed in public/_headers
    // and out of sitemap.xml and llms.txt until the model pages are signed off.
    path: '/models',
    load: () => import('./pages/ModelsPage').then((m) => m.ModelsPage),
    chunkSrc: 'src/pages/ModelsPage.tsx',
    sitemap: false,
    seo: {
      title: 'AI models we benchmarked | LangWatch',
      description: 'The popular AI models LangWatch has benchmarked, frontier and open, with their lab, open or closed weights, and the benchmarks they were tested in.',
    },
  },
  // PREVIEW: one page per registry model with a public result (registry.ts PAGE_MODELS), noindexed like /models.
  ...PAGE_MODELS.map(
    (m): RouteDef => ({
      path: modelHref(m.id),
      load: () => import('./pages/modelRoute').then((p) => p.modelPage(slugOf(m.id))),
      chunkSrc: 'src/pages/modelRoute.tsx',
      sitemap: false,
      seo: {
        title: `${m.name} benchmarks | LangWatch`,
        description: `${m.name}${m.provider ? ` by ${m.provider}` : ''}: every LangWatch benchmark result, per task, with 95% intervals, tie tiers, and where it is strong or weak.`,
      },
    }),
  ),
  {
    path: '/compare/jev-benchmark',
    load: () => import('./pages/JevBenchmarkPage').then((m) => m.JevBenchmarkPage),
    chunkSrc: 'src/pages/JevBenchmarkPage.tsx',
    seo: {
      keyphrase: 'jev benchmark',
      title: 'Jev benchmark: Jev vs tiny open models | LangWatch',
      description:
        'Jev against seven open models under 1B on 15 decision tasks: accuracy with 95% intervals, latency, contamination and data quality, measured by LangWatch.',
      image: '/images/jev-benchmark/og.webp',
      jsonLd: [
        {
          '@context': 'https://schema.org',
          '@type': 'Dataset',
          name: 'Jev-class decision model benchmark',
          description:
            'Aggregate results of the LangWatch benchmark of Jev and seven open Jev-class decision models under 1B parameters on 15 tasks: primary metric with 95% bootstrap intervals, tie tiers, coverage, consistency and latency per model (Jev has the highest p95 latency on 10 of 11 real tasks), with a contamination audit. The open models’ assumed cost is published; Jev’s is not shown on this release. Aggregates only, no item text.',
          url: `${SITE_URL}/compare/jev-benchmark`,
          creator: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
          isAccessibleForFree: true,
          keywords: ['Jev', 'decision models', 'LLM judge', 'benchmark', 'prompt injection', 'moderation', 'intent routing'],
        },
        faqJsonLd(jevBenchmarkFaq),
      ],
    },
  },
  {
    path: '/compare/jev-vs-all',
    load: () => import('./pages/JevVsAllPage').then((m) => m.JevVsAllPage),
    chunkSrc: 'src/pages/JevVsAllPage.tsx',
    seo: {
      // Hand-synced with src/components/jev-vs-all-page/content.ts (this table cannot import the page's
      // export). No counts here: "up to 28B" is pinned by the page test to the export's open systems.
      // a phrase the page body says (the lede), so the content check can find it
      keyphrase: 'open models up to 28B',
      title: 'Jev vs open models up to 28B, benchmarked | LangWatch',
      description:
        'Jev vs open models up to 28B on real decision tasks: accuracy with 95% intervals, tie tests, contamination and data quality, measured by LangWatch.',
      image: '/images/jev-vs-all/og.webp',
      jsonLd: [
        {
          '@context': 'https://schema.org',
          '@type': 'Dataset',
          name: 'Jev vs open decision models up to 28B',
          description:
            'Aggregate results of the LangWatch benchmark of Jev against open decision models up to 28B parameters on real decision tasks: primary metric with 95% bootstrap intervals, tie tests, coverage, consistency, and latency per model labelled with the GPU it ran on, with a contamination audit. Aggregates only, no item text.',
          url: `${SITE_URL}/compare/jev-vs-all`,
          creator: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
          isAccessibleForFree: true,
          keywords: ['Jev', 'open models', 'decision models', 'LLM judge', 'benchmark', 'prompt injection', 'moderation', 'intent routing'],
        },
        faqJsonLd(jevVsAllFaq),
      ],
    },
  },
  {
    path: '/compare/jev-vs-llm',
    load: () => import('./pages/JevVsLlmPage').then((m) => m.JevVsLlmPage),
    chunkSrc: 'src/pages/JevVsLlmPage.tsx',
    seo: {
      // Hand-synced with src/components/jev-vs-llm-page/content.ts (this table cannot import the page's export).
      // No counts here: the title's count is computed on the page from the export's tie tiers.
      // a phrase the page body says (the verdict, the subtitle), so the content check can find it
      keyphrase: 'best frontier LLM',
      title: 'Jev vs frontier LLMs: accuracy, latency and cost | LangWatch',
      description:
        'Jev against frontier LLMs from OpenAI, Google and Anthropic on real decision tasks: tie-tested accuracy, latency, and cost per prompt.',
      image: '/images/jev-vs-llm/og.webp',
      jsonLd: [
        {
          '@context': 'https://schema.org',
          '@type': 'Dataset',
          name: 'Jev vs frontier LLMs on decision tasks',
          description:
            'Aggregate results of the LangWatch benchmark of Jev against frontier LLMs on real decision tasks: primary metric with 95% bootstrap intervals and tie tests, latency with its measurement basis, and cost per prompt at list price. Aggregates only, no item text.',
          url: `${SITE_URL}/compare/jev-vs-llm`,
          creator: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
          isAccessibleForFree: true,
          keywords: ['Jev', 'frontier LLM', 'LLM judge', 'benchmark', 'prompt injection', 'moderation', 'intent routing', 'cost per prompt'],
        },
        faqJsonLd(jevVsLlmFaq),
      ],
    },
  },
  {
    path: '/launch-week-nov',
    load: () => import('./pages/LaunchWeekNovPage').then((m) => m.LaunchWeekNovPage),
    chunkSrc: 'src/pages/LaunchWeekNovPage.tsx',
    seo: {
      keyphrase: 'langwatch launch week',
      title: 'LangWatch Launch Week | Five days of releases',
      description:
        'Five days, five releases: Prompt Playground, RBAC, voice agent testing, Scenario MCP, and the Better Agents CLI. Everything from LangWatch Launch Week.',
    },
  },
  {
    path: '/llm-red-teaming',
    load: () => import('./pages/LLMRedTeamingPage').then((m) => m.LLMRedTeamingPage),
    chunkSrc: 'src/pages/LLMRedTeamingPage.tsx',
    seo: {
      keyphrase: 'llm red teaming',
      title: 'LLM red-teaming for AI agents | LangWatch',
      description:
        'Open-source adversarial testing for AI agents. Run 50-turn Crescendo attacks to surface jailbreaks, prompt extraction, and data exfiltration before they ship.',
    },
  },
  {
    // /prompt-optimizer is the live, indexed URL, kept as an alias so it never
    // 404s; /prompt-management is the canonical going forward.
    path: '/prompt-management',
    aliases: ['/prompt-optimizer'],
    load: () => import('./pages/PromptManagementPage').then((m) => m.PromptManagementPage),
    chunkSrc: 'src/pages/PromptManagementPage.tsx',
    seo: {
      keyphrase: 'prompt management',
      title: 'Prompt management | LangWatch',
      description:
        'Manage prompts as one versioned source of truth. Version, review through GitHub, A/B test in production, and tie every prompt to its traces. Prompts as code.',
    },
  },
  {
    path: '/research',
    load: () => import('./pages/ResearchIndexPage').then((m) => m.ResearchIndexPage),
    chunkSrc: 'src/pages/ResearchIndexPage.tsx',
    seo: {
      keyphrase: 'ai agent research',
      title: 'Research | LangWatch',
      description:
        'Measured studies of how AI agents behave in production. Full method, data, and figures for every finding, written up so the numbers can be checked.',
      jsonLd: [
        {
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: 'LangWatch Research',
          url: `${SITE_URL}/research`,
          publisher: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
          hasPart: researchPapers().map((p) => ({
            '@type': 'ScholarlyArticle',
            headline: p.meta.title,
            url: `${SITE_URL}/research/${p.slug}`,
            ...(p.meta.date ? { datePublished: p.meta.date } : {}),
          })),
        },
      ],
    },
  },
  {
    path: '/claude-code-usage',
    load: () => import('./pages/ClaudeCodeUsagePage').then((m) => m.ClaudeCodeUsagePage),
    chunkSrc: 'src/pages/ClaudeCodeUsagePage.tsx',
    seo: {
      keyphrase: 'claude code usage',
      title: 'Track your Claude Code usage | LangWatch',
      description:
        'Full trace history for Claude Code, Codex, OpenCode, Copilot, Cursor, and Pi. Tokens by class including cache reads, and theoretical spend on bundled plans.',
      jsonLd: [faqJsonLd(claudeCodeUsageFaq)],
    },
  },
  {
    path: '/codex-usage',
    load: () => import('./pages/ClaudeCodeUsagePage').then((m) => m.CodexUsagePage),
    chunkSrc: 'src/pages/ClaudeCodeUsagePage.tsx',
    seo: {
      keyphrase: 'codex usage',
      title: 'Track your Codex usage | LangWatch',
      description:
        'Full trace history for Codex sessions: every model turn and tool call, tokens by class including cache reads, and theoretical spend on bundled ChatGPT plans.',
      jsonLd: [faqJsonLd(codexUsageFaq)],
    },
  },
  {
    path: '/opencode-usage',
    load: () => import('./pages/ClaudeCodeUsagePage').then((m) => m.OpenCodeUsagePage),
    chunkSrc: 'src/pages/ClaudeCodeUsagePage.tsx',
    seo: {
      keyphrase: 'opencode usage',
      title: 'Track your OpenCode usage | LangWatch',
      description:
        'Full trace history for OpenCode sessions on any provider: model turns, tool calls, tokens by class including cache reads, and theoretical spend per model.',
      jsonLd: [faqJsonLd(openCodeUsageFaq)],
    },
  },
  {
    path: '/voice-ai-agent-testing',
    load: () => import('./pages/VoiceAgentTestingPage').then((m) => m.VoiceAgentTestingPage),
    chunkSrc: 'src/pages/VoiceAgentTestingPage.tsx',
    seo: {
      keyphrase: 'voice agent testing',
      title: 'Voice agent testing | LangWatch',
      description:
        'At-scale automated testing for voice and chat agents. Open-source simulation, latency and quality checks, and production monitoring for voice AI.',
    },
  },
  {
    path: '/llmops-conversational-voice-ai',
    load: () => import('./pages/ConversationalVoiceAIPage').then((m) => m.ConversationalVoiceAIPage),
    chunkSrc: 'src/pages/ConversationalVoiceAIPage.tsx',
    // Retired in favour of /voice-ai-agent-testing and 301'd at the Cloudflare
    // edge. Kept prerendered so the legacy-URL 404 contract still holds, but out
    // of the sitemap so search engines aren't pointed at a URL that redirects.
    sitemap: false,
    seo: {
      keyphrase: 'conversational voice ai',
      title: 'Conversational voice AI observability | LangWatch',
      description:
        'Observability and online evals for conversational voice AI. Trace every call, monitor latency and quality, and catch regressions in production.',
    },
  },
  {
    path: '/guides/evals-guide',
    load: () => import('./pages/evals-guide/EvalsGuidePage').then((m) => m.EvalsGuidePage),
    chunkSrc: 'src/pages/evals-guide/EvalsGuidePage.tsx',
    seo: {
      keyphrase: 'llm evals guide',
      title: 'The Evals Golden Guide | LangWatch',
      description:
        'A practical guide to evaluating LLM agents: why evals exist, the three eval types, the testing pyramid, and scenarios that catch real failure modes.',
    },
  },
  {
    path: '/guides/ai-agent-guide',
    load: () => import('./pages/agents-guide/AgentsGuidePage').then((m) => m.AgentsGuidePage),
    chunkSrc: 'src/pages/agents-guide/AgentsGuidePage.tsx',
    seo: {
      keyphrase: 'ai agents guide',
      title: 'The AI Agents Guide | LangWatch',
      description:
        'An interactive guide to building AI agents that survive production: use cases, frameworks, the agent testing pyramid, observability, and scenario simulation.',
    },
  },
  {
    path: '/blog',
    load: () => import('./pages/BlogIndexPage').then((m) => m.BlogIndexPage),
    chunkSrc: 'src/pages/BlogIndexPage.tsx',
    seo: {
      title: 'Blog | LangWatch',
      description:
        'Engineering deep-dives, product updates, and field lessons on evaluating, testing, and observing AI agents in production, from the LangWatch team.',
      feed: { href: '/blog/rss.xml', title: 'LangWatch Blog' },
    },
  },
  {
    path: '/changelog',
    load: () => import('./pages/ChangelogIndexPage').then((m) => m.ChangelogIndexPage),
    chunkSrc: 'src/pages/ChangelogIndexPage.tsx',
    seo: {
      title: 'Changelog | LangWatch',
      description:
        'Every LangWatch release, improvement, and fix as it ships. Follow the platform evolve across evals, simulations, observability, and prompt management.',
      feed: { href: '/changelog/rss.xml', title: 'LangWatch Changelog' },
    },
  },
]

// Bespoke pages above; blog/changelog/customer detail pages are generated from
// the MDX content collections (src/content/**) so every migrated URL resolves.
export const routes: RouteDef[] = [...staticRoutes, ...contentRoutes()]

function norm(p: string): string {
  // Tolerate the prerendered file's literal path: the build emits flat
  // `<route>.html` files, and static servers (Lighthouse CI's included) serve
  // them at that URL. Without this, `/customers.html` matched nothing, fell
  // back to the homepage route, and hydration replaced the whole prerendered
  // page with the homepage.
  const withoutHtml = decodePath(p).replace(/(\/index)?\.html$/, '')
  const c = withoutHtml.replace(/\/+$/, '')
  return c === '' ? '/' : c
}

// Slugs can contain non-ASCII characters (e.g. the "…" in
// /blog/eat-sleep-append-repeat…), which the browser percent-encodes in
// `location.pathname` ("%E2%80%A6"). Route paths hold the literal character, so
// decode both sides before comparing. Decoding an already-literal path is a
// no-op, so this is safe for plain ASCII routes too.
function decodePath(p: string): string {
  try {
    return decodeURIComponent(p)
  } catch {
    return p
  }
}

/** Resolve a pathname to a route, falling back to the homepage. */
export function matchRoute(pathname: string): RouteDef {
  const p = norm(pathname)
  return (
    routes.find((r) => norm(r.path) === p || r.aliases?.some((a) => norm(a) === p)) ??
    // Unknown path: the 404 route, so hydrating dist/404.html (served by the
    // host for every missing page) renders the 404 page, not the homepage.
    routes.find((r) => r.path === '/404') ??
    routes[0]
  )
}

/** All paths to prerender (canonical paths plus their aliases). */
export function prerenderPaths(): string[] {
  const out: string[] = []
  for (const r of routes) {
    out.push(r.path)
    for (const a of r.aliases ?? []) out.push(a)
  }
  return out
}

/** Canonical paths that belong in the sitemap. */
export function sitemapPaths(): string[] {
  return routes.filter((r) => r.sitemap !== false).map((r) => r.path)
}

/**
 * Truthful <lastmod> values for the sitemap, keyed by canonical path: content
 * pages get their frontmatter publish date. Static pages are omitted on
 * purpose; stamping them with the build time would mark the whole site
 * changed on every deploy and make lastmod useless for recrawl scheduling.
 */
export function sitemapLastmod(): Record<string, string> {
  const out: Record<string, string> = {}
  for (const e of allContent()) {
    if (e.meta.date) out[contentPath(e)] = e.meta.date
  }
  return out
}

/**
 * Content-quality target per canonical route (keyphrase + resolved page-type
 * profile), for the content-quality report. Routes without a keyphrase are
 * omitted. The profile defaults from the path and can be overridden per route
 * with `seo.contentType`.
 */
export function seoTargets(): Record<string, SeoTarget> {
  const out: Record<string, SeoTarget> = {}
  for (const r of routes) {
    const target = resolveSeoTarget(r.path, r.seo)
    if (target) out[r.path] = target
  }
  return out
}
